import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, dashboard, isForbidden } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { useNav } from '../../navigation.js';
import { OWN_CASELOAD } from '../../delivery.js';
import { FIGURE_PLACEHOLDER } from '../../figureState.js';
import { queueScopeNote } from '../queueScope.js';
import {
  BarChart,
  GroupedChart,
  LineChart,
  measuredPoints,
  type GroupedPoint,
} from '../analytics/charts.js';
import { changeLabel, changeTone, weekShortLabel } from '../analytics/analyticsRange.js';
import { ofLabel } from '../analytics/explain.js';
import { formatWidgetValue, metricDrilldownHref, vizLabel } from './metricFormat.js';
import { LoadFailure } from '../../LoadFailure.js';

type WidgetData = DashboardNS.WidgetData;
type TableData = DashboardNS.TableData;
type SeriesData = DashboardNS.SeriesData;
type KpiData = DashboardNS.KpiData;
type UnavailableData = DashboardNS.UnavailableData;
type GroupedData = DashboardNS.GroupedData;

/**
 * A single configurable-dashboard widget: fetches the curated metric data for
 * its (metric, visualization, period) and renders KPI / line / bar / table using
 * the shared chart + table furniture. No metric is fabricated — every figure is
 * the backend resolver's own output.
 */
export function WidgetCard({
  metricKey,
  label,
  visualization,
  preset,
  from,
  to,
  threshold,
  layoutId,
  widgetPosition,
}: {
  metricKey: string;
  label: string;
  visualization: DashboardNS.WidgetVisualization;
  preset: DashboardNS.AnalyticsPreset;
  from: string;
  to: string;
  threshold?: number;
  /**
   * WHICH SAVED LAYOUT THIS WIDGET BELONGS TO, and which widget of it.
   *
   * Both together, or neither. The server loads the layout's stored FILTERS
   * from `layoutId` and the widget's stored GROUPING from `widgetPosition` —
   * neither of which the client may supply, and the grouping in particular is
   * refused outright as a query parameter (a caller who could choose the
   * fan-out could slice a cohort into sub-minimum buckets and read off which
   * came back withheld). Omitting them does not draw a different chart; it
   * draws the widget as if it were not part of any dashboard, which for a
   * grouped widget is precisely the silent flattening the capability exists to
   * prevent.
   */
  layoutId?: string;
  widgetPosition?: number;
}) {
  const navigate = useNav();
  const query = useQuery({
    queryKey: [
      'dashboards',
      'widget',
      metricKey,
      visualization,
      preset,
      from,
      to,
      threshold,
      layoutId,
      widgetPosition,
    ],
    queryFn: () =>
      dashboard.dashboardWidgetData(metricKey, {
        preset,
        visualization,
        ...(threshold != null ? { threshold } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(layoutId ? { layoutId } : {}),
        ...(layoutId && widgetPosition != null ? { widgetPosition } : {}),
      }),
  });

  // A widget's own 403 (`routes.ts` refuses row-level widgets per-widget) was
  // taking the failure branch and offering a retry.
  if (query.isError && isForbidden(query.error)) {
    return (
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">{label}</h2>
        </div>
        <Refused
          heading="This widget is not shown to you"
          message="Your account does not have access to the figures behind it. Nothing has been left out of them — they are not yours to see."
        />
      </section>
    );
  }
  // A 422 is the server REFUSING this picture of this measure (a point-in-time
  // roster as a weekly line, a period too long to draw) in its own sentence.
  // An answer, not a fault and not a dead link: said, with no retry.
  if (query.isError && query.error instanceof ApiError && query.error.status === 422) {
    const said = (query.error.body as { message?: unknown } | null)?.message;
    return (
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">{label}</h2>
        </div>
        <p className="rn-muted" role="status">
          {typeof said === 'string' ? said : query.error.message}
        </p>
      </section>
    );
  }
  if (query.isLoading) return <SkeletonSet surface="band" />;
  if (query.isError && !query.data) {
    return (
      <LoadFailure
        error={query.error}
        message="We couldn’t load this widget just now, so read it as unknown rather than empty."
        onRetry={() => void query.refetch()}
      />
    );
  }
  const data = query.data?.data;
  if (!data) {
    // `return null` made the whole widget DISAPPEAR — no card, no title, no
    // explanation. In a grid of widgets a vanishing one is invisible: the
    // reader sees a smaller dashboard and no reason to doubt it.
    return (
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">{label}</h2>
        </div>
        <p className="rn-muted" role="status">
          This widget has not been answered yet.
        </p>
      </section>
    );
  }

  // WHAT "VIEW DETAILS" OFFERS, AND THE TWO PAYLOADS IT IS A LIE ABOUT.
  //
  // A drilldown says: there are rows behind this figure, and here is where to
  // read them. Two of the four `WidgetData` members have nothing behind them,
  // and a way deeper contradicts what the card has just finished saying:
  //
  //   `unavailable` — the server answered "there is no figure, and here is
  //                   why". A link onward implies the figure is on the far side
  //                   of that sentence. It is nowhere.
  //   a table whose `rowScope.population` is `none` — the reader reaches
  //                   NOBODY, and the caption over the rows says so in the
  //                   server’s words. The screen the link leads to is narrowed
  //                   by the same access and counts nobody either, so beneath
  //                   that sentence the button reads as a way round it.
  //
  // Same rule as the empty-vs-withheld split in `Table` below: what a reader
  // cannot reach is not dressed up as reachable.
  const href = metricDrilldownHref(metricKey);
  const open = href && hasSomethingBehindIt(data) ? () => navigate(href) : undefined;

  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">{label}</h2>
        <span className="rn-muted">{vizLabel(visualization)}</span>
      </div>
      {renderData(data, open)}
      {open && data.kind !== 'kpi' ? (
        <div style={{ marginTop: 'var(--space-12)' }}>
          <button type="button" className="rn-link" onClick={open}>
            View details →
          </button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Whether a drilldown into this payload leads anywhere at all. Total over the
 * union for the same reason `renderData` is: a fifth member has to answer it.
 */
function hasSomethingBehindIt(data: WidgetData): boolean {
  switch (data.kind) {
    case 'kpi':
    case 'series':
      return true;
    case 'table':
      return data.rowScope.population !== 'none';
    // A BREAKDOWN IS ALREADY THE ROWS. The drilldown screens are the ungrouped
    // ones, so "View details" under a per-program chart would lead AWAY from the
    // split the reader is looking at rather than deeper into it — a link that
    // undoes the widget. When one of those screens can be opened at a named
    // group this becomes a real offer; until then it is not made.
    case 'grouped':
      return false;
    case 'unavailable':
      return false;
  }
}

/**
 * THE SWITCH THE PAYLOAD’S OWN DOCUMENTATION ASKS FOR.
 *
 * `WidgetData` has FOUR members and this handled three, falling through on
 * `unavailable` and returning `undefined`. React renders `undefined` as
 * nothing, so a widget the server had answered with "there is no figure, and
 * here is why" drew a titled card with an empty body — the one payload written
 * to make a missing figure impossible to fake, made impossible to see.
 *
 * THE RETURN TYPE IS THE ENFORCEMENT, and it is why there is no `default` arm.
 * `ReactElement` does not include `undefined`, so an unhandled member leaves a
 * path to the end of this function and the compiler refuses it (TS2366) at the
 * declaration — a fifth member cannot join the union without this file failing
 * to build. A `default` returning a fallback would compile forever and put the
 * blank card back.
 */
function renderData(data: WidgetData, onOpen?: () => void): ReactElement {
  switch (data.kind) {
    case 'kpi':
      return <Kpi data={data} onOpen={onOpen} />;
    case 'series':
      return <Series data={data} />;
    case 'table':
      return <Table data={data} onOpen={onOpen} />;
    case 'grouped':
      return <Grouped data={data} />;
    case 'unavailable':
      return <Unavailable data={data} />;
  }
}

/**
 * WHAT THE GROUPS ARE CALLED, in the words a reader uses.
 *
 * Derived from the dimension the SERVER reported grouping by, never from the
 * widget's own request — the payload's `groupedBy` is the answer and the stored
 * `groupBy` is the question, and captioning from the question is how a chart
 * goes on saying "by program" after the engine started answering something
 * else.
 */
const GROUP_HEADER: Record<GroupedData['groupedBy'], string> = {
  program: 'Program',
  site: 'Site',
  day: 'Day',
  week: 'Week',
};

/**
 * THE FIFTH MEMBER — a saved widget drawn as the breakdown it was approved as
 * (F307).
 *
 * TWO SENTENCES FOR ONE BLANK, AND THE PAYLOAD IS THE ONLY THING THAT CAN TELL
 * THEM APART. A `null` in `rows` means either "nothing was recorded for this
 * group" or "a real figure is withheld from you because too few people stand
 * behind it", and those are not interchangeable: a reader told the first about a
 * program that is really the second has been told something false. The join is
 * `disclosure.suppression.cells[].fields`, which names the row's own `key` — so
 * the reason is looked up per group and travels into the chart on the point,
 * where the visually hidden data table renders it as a word instead of an empty
 * cell.
 *
 * AND THE SAME FACT IS SAID ON SCREEN, not only to a screen reader. Someone
 * looking at the chart sees a dashed gap where a bar would be; the note above it
 * says how many groups are in that state and why, in the server's own
 * vocabulary (`minCohortSize`). Without it the gap is a mystery to exactly the
 * reader who can see it.
 */
function Grouped({ data }: { data: GroupedData }) {
  const fmt = (v: number) => formatWidgetValue(v, data.unit, data.precision);
  const groupHeader = GROUP_HEADER[data.groupedBy];

  // The keys the server declared withheld, flattened once. `fields` is a BLOCK
  // — the groups in it determine each other through the published total — so
  // membership is what matters here, not which cell a key sits in.
  const cells = data.disclosure.suppression?.cells ?? [];
  const withheld = new Set(cells.flatMap((cell) => cell.fields));
  const floor = data.disclosure.suppression?.minCohortSize;

  const points: GroupedPoint[] = data.rows.map((row) => ({
    key: row.key,
    label: row.label,
    value: row.value,
    absence: withheld.has(row.key)
      ? 'withheld — too few people stand behind this figure to report it'
      : 'no value recorded',
  }));

  const hiddenCount = points.filter((p) => p.value === null && withheld.has(p.key)).length;

  return (
    <>
      {hiddenCount > 0 ? (
        <p className="rn-muted" role="note">
          {`${hiddenCount} ${hiddenCount === 1 ? groupHeader.toLowerCase() : `${groupHeader.toLowerCase()}s`} `}
          {floor != null
            ? `are not shown: fewer than ${floor} people stand behind those figures, and enough of the rest are held back that the missing ones cannot be worked out from the total.`
            : 'are not shown, because too few people stand behind those figures.'}
        </p>
      ) : null}
      {data.chart === 'table' ? (
        <GroupedTable data={data} groupHeader={groupHeader} points={points} format={fmt} />
      ) : (
        <GroupedChart
          points={points}
          chart={data.chart}
          label={data.label}
          groupHeader={groupHeader}
          formatValue={fmt}
        />
      )}
    </>
  );
}

/**
 * The same breakdown when the saved widget asked for a TABLE.
 *
 * No hidden twin here, and that is the point: the table IS the picture, so its
 * rows are already in the accessibility tree. A `null` still renders its own
 * sentence rather than an em-dash, for the same reason the hidden table does —
 * a dash is one mark for two different facts.
 */
function GroupedTable({
  data,
  groupHeader,
  points,
  format,
}: {
  data: GroupedData;
  groupHeader: string;
  points: GroupedPoint[];
  format: (v: number) => string;
}) {
  const columns: Column<{ group: string; value: string }>[] = [
    { key: 'group', header: groupHeader, render: (r) => r.group },
    { key: 'value', header: data.label, numeric: true, render: (r) => r.value },
  ];
  const rows = points.map((p) => ({
    group: p.label,
    value: p.value === null ? p.absence : format(p.value),
  }));
  if (rows.length === 0) {
    return (
      <SuccessEmpty heading="Nothing in range" message="No groups to break this figure down by." />
    );
  }
  return <TableCard columns={columns} rows={rows} rowKey={(r) => r.group} />;
}

/**
 * THE FOURTH MEMBER, RENDERED IN THE SERVER’S OWN WORDS.
 *
 * `UnavailableData` carries no `value`, no `series` and no `rows`: the union
 * makes "there is no figure" a SHAPE rather than a flag beside a number,
 * precisely so a renderer cannot print the zero it would otherwise find lying
 * around. What it does carry is `message`, written once per reason server-side
 * so that two screens explaining the same blank cannot word it differently —
 * so it is rendered VERBATIM, and `reason` is not spelled out beside it,
 * because a label of this screen’s own devising would be that second wording.
 *
 * THE PLACEHOLDER IS NOT A ZERO. `FIGURE_PLACEHOLDER.absent` is the glyph this
 * product uses for a figure that is not a number, and the reason it is never
 * `0`: a rate with no denominator printed as `0%` is a claim that the work was
 * not done, made out of a question nobody could answer.
 */
function Unavailable({ data }: { data: UnavailableData }) {
  return (
    <div className="db-kpi" style={{ boxShadow: 'none' }}>
      <div className="db-kpi-label">{data.label}</div>
      <div className="db-kpi-value">{FIGURE_PLACEHOLDER.absent}</div>
      <p className="rn-muted" role="note" style={{ margin: 'var(--space-8) 0 0' }}>
        {data.message}
      </p>
    </div>
  );
}

function Kpi({ data, onOpen }: { data: KpiData; onOpen?: () => void }) {
  // A rate states its two counts ("88 of 107 (82%)"), the percent computed from them.
  const value = data.ratio
    ? ofLabel(data.ratio.numerator, data.ratio.denominator, (v) => formatWidgetValue(v, 'count', 0))
    : formatWidgetValue(data.value, data.unit, data.precision);
  const prior =
    data.prior == null ? null : formatWidgetValue(data.prior, data.unit, data.precision);
  const body = (
    <>
      <div className="db-kpi-label">{data.label}</div>
      <div className="db-kpi-value">{value}</div>
      <div className="db-kpi-foot">
        <span className="rn-muted">{prior != null ? `prior ${prior}` : '—'}</span>
        {data.breached != null ? (
          <Pill variant={data.breached ? 'look' : 'good'} dot>
            {data.breached ? 'below target' : 'on target'}
          </Pill>
        ) : (
          <Pill variant={changeTone(data.direction)} dot>
            {changeLabel(data.changePct, data.direction)}
          </Pill>
        )}
      </div>
    </>
  );
  if (!onOpen) {
    return (
      <div className="db-kpi" style={{ boxShadow: 'none' }}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="db-kpi"
      onClick={onOpen}
      style={{ boxShadow: 'none', textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
    >
      {body}
    </button>
  );
}

function Series({ data }: { data: SeriesData }) {
  const labels = data.chart === 'line' ? data.labels.map(weekShortLabel) : data.labels;
  const fmt = (v: number) => formatWidgetValue(v, data.unit, data.precision);
  if (data.chart === 'line') {
    return (
      <LineChart
        labels={labels}
        coverage={data.coverage}
        formatValue={fmt}
        series={data.series.map((s, i) => ({
          name: s.name,
          color: SERIES_COLORS[i % SERIES_COLORS.length]!,
          points: measuredPoints(s.values),
        }))}
      />
    );
  }
  return <BarChart labels={labels} values={data.series[0]?.values ?? []} formatValue={fmt} />;
}

const SERIES_COLORS = [
  'var(--color-sky)',
  'var(--color-navy)',
  'var(--color-good)',
  'var(--color-unsure)',
  'var(--color-look)',
];

/**
 * WHOSE ROWS THESE ARE.
 *
 * `TableData.rowScope` is a `QueueScope` and the server stamps it on EVERY
 * table widget — three of the four row-level tables are narrowed by the
 * supervisor edge, by the reader's program grants, or by both
 * (`dashboard/accessPolicy.ts`). This component is the one consumer of that
 * payload, and it read `columns` and `rows` and nothing else, so a table
 * narrowed to a supervisor's own reports rendered with NO CAPTION AT ALL —
 * indistinguishable, in a grid of widgets on a dashboard the reader configured
 * themselves, from an organization-wide one.
 *
 * `OWN_CASELOAD` is the caseload noun because these tables are PER-WORKER: the
 * rows are the workers, and `worker-scope.ts` narrows them to the reader's own
 * row plus their direct reports. That is the population `delivery.ts` already
 * has one sentence for, so this widget grid and the landing screen's delivery
 * band describe it identically.
 *
 * THE EMPTY TABLE AND THE WITHHELD TABLE ARE NOT THE SAME SCREEN. Zero rows
 * used to be a `SuccessEmpty` unconditionally — "Nothing in range" — which is a
 * claim that this period produced nothing. Under `population: 'none'` the
 * reader was shown NOBODY, and the server says why; presenting that as a
 * successful empty result is the exact inversion the frame exists to prevent,
 * and it is made in the reassuring direction. So the `none` arm renders the
 * server's own sentence and no success heading, and `SuccessEmpty` is left to
 * the case it was written for: a population that really was searched.
 */
function Table({ data, onOpen }: { data: TableData; onOpen?: () => void }) {
  const scopeNote = queueScopeNote(data.rowScope, OWN_CASELOAD);
  const countedNobody = data.rowScope.population === 'none';
  const columns: Column<Record<string, string | number | null>>[] = data.columns.map((c) => ({
    key: c.key,
    header: c.header,
    numeric: c.numeric,
    render: (row) => {
      const v = row[c.key];
      if (typeof v === 'number') return String(v);
      // A null cell has no flag beside it — `TableData.rows` is
      // `Record<string, string | number | null>` — so the em dash cannot say
      // whether it is an absence or a withholding. It is left as a dash and
      // recorded in the report rather than guessed at here.
      return v ?? '—';
    },
  }));
  // Zero rows through `TableCard` is a header over an empty `<tbody>` — a
  // widget that looks broken rather than one that has nothing to show.
  //
  // The caller guards the PERMISSION; it does not guard the SCOPE. The rows are
  // narrowed server-side, and `rowScope` above is now how far. The message keeps
  // its hedge because the narrowing it hedges against is real for the `subset`
  // arm and costs the `organization` arm only a clause.
  const body =
    data.rows.length === 0 ? (
      countedNobody ? null : (
        <SuccessEmpty
          heading="Nothing in range"
          message="Nothing matched this period within what your access reaches."
        />
      )
    ) : (
      <TableCard
        columns={columns}
        rows={data.rows}
        rowKey={(r) => JSON.stringify(r)}
        onRowOpen={onOpen}
      />
    );

  return (
    <>
      {/* Above the rows, not below them. A reader who has already read the
          table and moved on has been misinformed, and a caption they reach
          second cannot undo that — same placement as the frame over the
          landing screen's paperwork and reconciliation counts. */}
      {scopeNote !== null ? (
        <p className="rn-muted" role="note" style={{ margin: '0 0 var(--space-8)' }}>
          {scopeNote}
        </p>
      ) : null}
      {body}
    </>
  );
}
